import { Component, ElementRef, Input, OnChanges, OnInit, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { AttachedFile } from '../../models/attached-file';
import { MatExpansionPanel } from '@angular/material/expansion';
import { FormGroup, Validators } from '@angular/forms';
import { MimeTypePipe } from '../../pipes/mime-type.pipe';
import { AppStore, SnackbarErrorAction, SnackbarWarningAction } from '@alfresco/aca-shared/store';
import { Store } from '@ngrx/store';
import { MIMETYPES } from '../../models/mime-types';
import { AldenService } from '../../services/alden.service';
import { APP_CONFIG_DATE_VALUES, APP_CONFIG_FILE_SIZE_LIMIT } from '../../models/alden-constants';
import { WatermarkParams } from '../../models/watermark-params';

@Component({
  selector: 'smt-file-input',
  templateUrl: './file-input.component.html',
  styleUrls: ['./file-input.component.scss'],
})
export class FileInputComponent implements OnInit, OnChanges {
  @Input() label: string;
  @Input() group: FormGroup<any>;
  @Input() controlName: string;
  @Input() files: AttachedFile[];
  @Input() maxFiles: number;
  @Input() editableFields: string[];
  @Input() mandatoryFields: string[];
  @Input() direction: string;
  @Input() smallSize: boolean;
  @Input() mimeTypes: string[];
  @Input() interacted: boolean;
  @Input() watermarkParams: WatermarkParams | undefined;
  @Input() downloadEnabled: boolean = false;
  @Input() allowedDownloadGroups: string[] | undefined;

  @ViewChildren(MatExpansionPanel) panels: QueryList<MatExpansionPanel>;
  @ViewChild("fileDropRef", { static: false }) fileDropEl: ElementRef;

  existingIDs: string[];
  disabled: boolean;

  fileSizeLimit: number;
  defaultDateTimeFormat: string;

  constructor(protected mimeTypePipe: MimeTypePipe, protected store: Store<AppStore>, public aldenService: AldenService) {
    this.files = [];
    this.existingIDs = [];
    this.direction = "row";
    this.smallSize = false;
    this.disabled = false;
    this.mandatoryFields = ['name'];
    this.mimeTypes = [];
  }

  ngOnChanges(changes: SimpleChanges): void {
    const editableFieldsChange = changes["editableFields"];
    if(editableFieldsChange !== undefined) {
      if(!this.editableFields?.includes('title')) {
        this.files?.forEach((file) => file.title = undefined);
      }
      if(!this.editableFields?.includes('description')) {
        this.files?.forEach((file) => file.description = undefined);
      }
    }

    const mandatoryFieldsChange = changes["mandatoryFields"];
    if(mandatoryFieldsChange !== undefined) {
      this.mandatoryFields = mandatoryFieldsChange.currentValue ? [...mandatoryFieldsChange.currentValue, 'name'] : ['name'];
    }

    this.checkMandatory();
  }

  ngOnInit(): void {
    this.disabled = this.group.get(this.controlName).disabled;

    // Obtenemos el limite de tamaño (MB)
    this.fileSizeLimit = this.aldenService.getAppConfigProp(APP_CONFIG_FILE_SIZE_LIMIT);
    this.defaultDateTimeFormat = this.aldenService.getAppConfigProp(APP_CONFIG_DATE_VALUES).defaultDateTimeFormat;

    // Obtener el valor inicial
    this.files = this.group.controls[this.controlName].getRawValue();

    // Escuchar cambios
    this.group.controls[this.controlName].valueChanges.subscribe(value => {
      this.files = value;

      // Comprobamos limite de tamaño
      if(this.fileSizeLimit) {
        const totalBytes = this.files.filter((f) => !f.uuid).reduce((sum, attach) => sum + attach.size, 0);
        const totalMB = totalBytes / (1024 * 1024);
        const currentErrors = this.group.controls[this.controlName].errors || {};
        if(totalMB > this.fileSizeLimit) {
            this.group.controls[this.controlName].setErrors({ ...currentErrors, maxSize: true });
            this.store.dispatch(new SnackbarWarningAction('ALDEN.MESSAGES.ERRORS.FILE_SIZE_LIMIT', { fileSizeLimit: this.fileSizeLimit }));
        } else {
          const { maxSize, ...otherErrors } = currentErrors;
          const hasOtherErrors = Object.keys(otherErrors).length > 0;
          this.group.controls[this.controlName].setErrors(hasOtherErrors ? otherErrors : null);
        }
      }
    });

    if(this.downloadEnabled)
      this.isDownloadAllowed().then(res => this.downloadEnabled = res!!).catch(() => this.downloadEnabled = false);
  }

  sendData() {
    this.group.get(this.controlName).setValue(this.files);
    this.checkMandatory();
    this.markAsTouched();
  }

  markAsTouched() {
    this.group.get(this.controlName).markAsTouched();
  }

  changeName(index: number) {
    const file = this.files[index];
    const mimeType = this.mimeTypePipe.transform(file.mimeType);

    // Obtener la parte antes del último punto
    const lastDotIndex = file.name.lastIndexOf(".");
    const baseName = lastDotIndex !== -1 ? file.name.substring(0, lastDotIndex) : file.name;

    // Verificar si la extensión ha sido modificada o eliminada
    const extensions = Array.isArray(mimeType.extension) ? mimeType.extension : [mimeType.extension];
    if (!extensions.some(ext => file.name.endsWith(ext))) {
      file.name = (baseName.trim() ? baseName : "untitled") + extensions[0];

      // Crear una nueva referencia al objeto
      this.files[index] = { ...file }; 
    }

    // Si el nombre base se queda vacio, ponerle nombre por defecto
    if(!baseName || file.name === mimeType.extension) {
      file.name = "untitled" + (index > 0 ? '_' + index : '') + mimeType.extension;
    }

    this.sendData();
  }

  setCursorPosition(event: FocusEvent, index: number) {
    // Poner el cursos en el nombre base (sin extension)
    const input = event.target as HTMLInputElement;
    const file = this.files[index];
    const mimeType = this.mimeTypePipe.transform(file.mimeType);

    // Calcular la posición antes de la extensión
    let cursorPosition = -1;

    const extensions = Array.isArray(mimeType.extension) ? mimeType.extension : [mimeType.extension];
    extensions.forEach(ext => {
      const pos = file.name.lastIndexOf(ext);
      if (pos > cursorPosition) {
        cursorPosition = pos;
      }
    });

    // Establecer la posición del cursor en el siguiente ciclo de renderizado
    if (cursorPosition !== -1) {
      requestAnimationFrame(() => {
        input.setSelectionRange(cursorPosition, cursorPosition);
      });
    }
  }

  isAnyPanelExpanded() {
    return this.panels.some((panel) => panel.expanded);
  }

  onFileDropped(fileList: FileList) {
    this.prepareFilesList(fileList);
  }

  fileBrowseHandler(event: Event) {
    const input = event.target as HTMLInputElement;
    this.prepareFilesList(input.files);
  }

  deleteFile(index: number) {
    this.files.splice(index, 1);
    this.sendData();
  }

  prepareFilesList(fileList: FileList) {
    const files = Array.from(fileList);
    this.fileDropEl.nativeElement.value = "";

    if(this.maxFiles && files.length > this.maxFiles) {
      this.store.dispatch(new SnackbarWarningAction('ALDEN.MESSAGES.ERRORS.MAX_FILES', { size: this.maxFiles }));
      return;
    }

    for (const file of files) {
      const fileId = this.generateUniqueID();
      const attachedFile = new AttachedFile(file.name, fileId, file);
      attachedFile.mimeType = file.type;
      attachedFile.size = file.size;
      attachedFile.lastModDate = file.lastModified;

      if(this.mimeTypes?.length > 0 && !this.mimeTypes.includes(file.type)) {
        const type = MIMETYPES[file.type]?.description ?? file.type;
        this.store.dispatch(new SnackbarWarningAction('ALDEN.MESSAGES.ERRORS.MIMETYPE_FILES', { type }));
      } else  {
        if(!this.files) 
          this.files = [attachedFile];
        else 
          this.files.push(attachedFile);
      }
    }

    this.sendData();
  }

  generateUniqueID() {
    let newID: string;
    do {
      newID = this.generateUUID();
    } while (this.existingIDs.includes(newID));
    this.existingIDs.push(newID);
    return newID;
  }

  generateUUID() {
    if (window.crypto && 'randomUUID' in window.crypto) {
      return crypto.randomUUID();
    }
    // Fallback
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = crypto.getRandomValues(new Uint8Array(1))[0] & 15;
      const v = c === 'x' ? r : (r & 0x3) | 8;
      return v.toString(16);
    });
  }

  checkMandatory() {
    const control = this.group.get(this.controlName);
    const errors = control.errors || {};

    // Verificar si el campo es requerido y está vacío
    const isEmpty = !control.value || control.value.length === 0;

    // Setear o eliminar el error si el campo tiene un valor
    if (this.isRequired && isEmpty) {
      errors.required = true;
    } else {
      delete errors.required; 
    }

    this.mandatoryFields?.forEach((field) => { 
      const isValid = this.files?.every((file) => file[field]);
      
      // Setear o eliminar el error si el campo tiene un valor
      if (!isValid) {
          errors[field] = true;
      } else {
          delete errors[field];
      }
    });

    control.setErrors(Object.keys(errors).length ? errors : null);
  }

  get isRequired(): boolean {
    const control = this.group.get(this.controlName);
    return control.hasValidator(Validators.required);
  }

  get isErrorVisile(): boolean {
    const control = this.group.get(this.controlName);
    return control.hasError('required') && (control.touched || control.dirty || this.interacted);
  }

  downloadFile(uuid: string) {
    if(this.watermarkParams)
      this.aldenService.downloadWatermarkedFile(uuid,this.watermarkParams!);
    else
      this.store.dispatch(new SnackbarErrorAction("ALDEN.MESSAGES.ERROR.PARAMS_NOT_FOUND"));
  }

  async isDownloadAllowed(){
    const profile = await this.aldenService.getProfileState();
    return !this.allowedDownloadGroups || profile.isAdmin || profile.groups?.some(group => this.allowedDownloadGroups?.includes(group.id));
  }
}